import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

const VIEWPORT_PADDING = 8;
const FALLBACK_SUBMENU_WIDTH = 220;

function buildWorkflowLabels(context) {
  if (context?.type === 'surface') {
    return {
      edit: 'Workflow bearbeiten',
      delete: 'Workflow löschen',
    };
  }

  if (context?.workflowType === 'regular') {
    return {
      edit: 'Routine bearbeiten',
      delete: 'Routine löschen',
    };
  }

  return {
    edit: 'Tages-Workflow bearbeiten',
    delete: 'Tages-Workflow löschen',
  };
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function measureMenuWidth(menuElement, itemSelector) {
  if (!menuElement) {
    return null;
  }

  const contentNodes = Array.from(menuElement.querySelectorAll(itemSelector));
  if (!contentNodes.length) {
    return null;
  }

  const maxContentWidth = contentNodes.reduce((maxWidth, node) => {
    const rect = node.getBoundingClientRect();
    return Math.max(maxWidth, rect.width);
  }, 0);

  const menuStyle = window.getComputedStyle(menuElement);
  const menuExtra =
    parseFloat(menuStyle.paddingLeft || '0')
    + parseFloat(menuStyle.paddingRight || '0')
    + parseFloat(menuStyle.borderLeftWidth || '0')
    + parseFloat(menuStyle.borderRightWidth || '0');

  const sampleButton = menuElement.querySelector('.timeline-context-menu-measure-button');
  const buttonStyle = sampleButton ? window.getComputedStyle(sampleButton) : null;
  const rowExtra = buttonStyle
    ? (
      parseFloat(buttonStyle.paddingLeft || '0')
      + parseFloat(buttonStyle.paddingRight || '0')
      + parseFloat(buttonStyle.borderLeftWidth || '0')
      + parseFloat(buttonStyle.borderRightWidth || '0')
    )
    : 0;

  return Math.ceil(maxContentWidth + menuExtra + rowExtra);
}

export default function TimelineContextMenu({
  open,
  x = 0,
  y = 0,
  context = null,
  onClose,
  onCreateDayWorkflow,
  onCreateRegularWorkflow,
  onEditTask,
  onShiftWorkflow,
  onShiftDay,
  onEditWorkflow,
  onDeleteWorkflow,
}) {
  const menuRef = useRef(null);
  const submenuRef = useRef(null);

  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [position, setPosition] = useState({ left: x, top: y, ready: false });
  const [submenuDirection, setSubmenuDirection] = useState('right');
  const [dimensions, setDimensions] = useState({ menuWidth: null, submenuWidth: null });

  const labels = useMemo(() => buildWorkflowLabels(context), [context]);
  const isSurface = context?.type === 'surface';
  const isTask = context?.type === 'task';
  const canEditWorkflow = !isSurface && Boolean(context?.workflowRowId);
  const canDeleteWorkflow = canEditWorkflow;
  const canShiftWorkflow = canEditWorkflow;
  const canShiftDay = isSurface;
  const canEditTask = isTask && Boolean(context?.taskId);

  useLayoutEffect(() => {
    setSubmenuOpen(false);
    setPosition({ left: x, top: y, ready: false });
    setSubmenuDirection('right');
    setDimensions({ menuWidth: null, submenuWidth: null });
  }, [open, x, y, context?.type, context?.workflowRowId, context?.taskId]);

  useLayoutEffect(() => {
    if (!open || !menuRef.current || typeof window === 'undefined') {
      return;
    }

    const measuredMenuWidth =
      measureMenuWidth(menuRef.current, '[data-menu-measure="main"]')
      ?? Math.ceil(menuRef.current.getBoundingClientRect().width);

    const measuredSubmenuWidth = submenuRef.current
      ? (
        measureMenuWidth(submenuRef.current, '[data-menu-measure="submenu"]')
        ?? Math.ceil(submenuRef.current.getBoundingClientRect().width)
      )
      : FALLBACK_SUBMENU_WIDTH;

    const clampedMenuWidth = Math.min(
      measuredMenuWidth,
      Math.max(160, window.innerWidth - (VIEWPORT_PADDING * 2)),
    );
    const clampedSubmenuWidth = Math.min(
      measuredSubmenuWidth,
      Math.max(160, window.innerWidth - (VIEWPORT_PADDING * 2)),
    );

    const menuRect = menuRef.current.getBoundingClientRect();
    const maxLeft = Math.max(VIEWPORT_PADDING, window.innerWidth - clampedMenuWidth - VIEWPORT_PADDING);
    const maxTop = Math.max(VIEWPORT_PADDING, window.innerHeight - menuRect.height - VIEWPORT_PADDING);

    const nextLeft = clamp(x, VIEWPORT_PADDING, maxLeft);
    const nextTop = clamp(y, VIEWPORT_PADDING, maxTop);
    const availableRight = window.innerWidth - (nextLeft + clampedMenuWidth) - VIEWPORT_PADDING;
    const shouldOpenLeft = availableRight < clampedSubmenuWidth + VIEWPORT_PADDING;

    setDimensions((current) => {
      if (
        current.menuWidth === clampedMenuWidth
        && current.submenuWidth === clampedSubmenuWidth
      ) {
        return current;
      }

      return {
        menuWidth: clampedMenuWidth,
        submenuWidth: clampedSubmenuWidth,
      };
    });

    setPosition((current) => {
      if (
        current.left === nextLeft
        && current.top === nextTop
        && current.ready
      ) {
        return current;
      }

      return {
        left: nextLeft,
        top: nextTop,
        ready: true,
      };
    });
    setSubmenuDirection(shouldOpenLeft ? 'left' : 'right');
  }, [open, submenuOpen, x, y]);

  if (!open) {
    return null;
  }

  function handleAction(fn, enabled) {
    if (!enabled || typeof fn !== 'function') {
      return;
    }

    fn(context);
    onClose?.();
  }

  return (
    <div
      ref={menuRef}
      className="timeline-context-menu dropdown-menu dropdown-menu-dark show"
      style={{
        position: 'fixed',
        left: `${position.left}px`,
        top: `${position.top}px`,
        display: 'block',
        zIndex: 2000,
        visibility: position.ready ? 'visible' : 'hidden',
        width: dimensions.menuWidth ? `${dimensions.menuWidth}px` : undefined,
      }}
      role="menu"
      data-testid="timeline-context-menu"
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div
        className={[
          'timeline-context-menu-submenu-wrap',
          submenuDirection === 'left' ? 'opens-left' : '',
        ].join(' ')}
      >
        <button
          type="button"
          className="dropdown-item timeline-context-menu-measure-button timeline-context-menu-submenu-toggle"
          onClick={() => setSubmenuOpen((current) => !current)}
          aria-expanded={submenuOpen ? 'true' : 'false'}
          aria-haspopup="menu"
          data-testid="timeline-context-create-toggle"
        >
          <span className="timeline-context-menu-row-content" data-menu-measure="main">
            <span className="timeline-context-menu-row-main">
              <i className="bi bi-plus-lg timeline-context-menu-item-icon" />
              Neu
            </span>
            <i
              className={[
                'bi',
                submenuDirection === 'left' ? 'bi-chevron-left' : 'bi-chevron-right',
                'timeline-context-menu-submenu-icon',
              ].join(' ')}
            />
          </span>
        </button>

        {submenuOpen ? (
          <div
            ref={submenuRef}
            className={[
              'timeline-context-menu-submenu',
              'dropdown-menu',
              'dropdown-menu-dark',
              'show',
              submenuDirection === 'left' ? 'is-left' : 'is-right',
            ].join(' ')}
            style={{
              width: dimensions.submenuWidth ? `${dimensions.submenuWidth}px` : undefined,
            }}
            role="menu"
          >
            <button
              type="button"
              className="dropdown-item timeline-context-menu-measure-button"
              onClick={() => handleAction(onCreateDayWorkflow, true)}
              data-testid="timeline-context-create-day-workflow"
            >
              <span className="timeline-context-menu-row-content" data-menu-measure="submenu">
                Tages-Workflow
              </span>
            </button>

            <button
              type="button"
              className="dropdown-item timeline-context-menu-measure-button"
              onClick={() => handleAction(onCreateRegularWorkflow, true)}
              data-testid="timeline-context-create-regular-workflow"
            >
              <span className="timeline-context-menu-row-content" data-menu-measure="submenu">
                Routine
              </span>
            </button>
          </div>
        ) : null}
      </div>

      {isTask ? (
        <button
          type="button"
          className={`dropdown-item timeline-context-menu-measure-button${canEditTask ? '' : ' disabled'}`}
          onClick={() => handleAction(onEditTask, canEditTask)}
          disabled={!canEditTask}
          aria-disabled={canEditTask ? 'false' : 'true'}
        >
          <span className="timeline-context-menu-row-content" data-menu-measure="main">
            <i className="bi bi-pencil-square timeline-context-menu-item-icon" />
            Task bearbeiten
          </span>
        </button>
      ) : null}

      {canShiftDay ? (
        <button
          type="button"
          className="dropdown-item timeline-context-menu-measure-button"
          onClick={() => handleAction(onShiftDay, true)}
        >
          <span className="timeline-context-menu-row-content" data-menu-measure="main">
            <i className="bi bi-arrow-left-right timeline-context-menu-item-icon" />
            Alle Workflows verschieben
          </span>
        </button>
      ) : null}

      {!isSurface ? (
        <button
          type="button"
          className={`dropdown-item timeline-context-menu-measure-button${canShiftWorkflow ? '' : ' disabled'}`}
          onClick={() => handleAction(onShiftWorkflow, canShiftWorkflow)}
          disabled={!canShiftWorkflow}
          aria-disabled={canShiftWorkflow ? 'false' : 'true'}
        >
          <span className="timeline-context-menu-row-content" data-menu-measure="main">
            <i className="bi bi-arrow-left-right timeline-context-menu-item-icon" />
            Workflow verschieben
          </span>
        </button>
      ) : null}

      <button
        type="button"
        className={`dropdown-item timeline-context-menu-measure-button${canEditWorkflow ? '' : ' disabled'}`}
        onClick={() => handleAction(onEditWorkflow, canEditWorkflow)}
        disabled={!canEditWorkflow}
        aria-disabled={canEditWorkflow ? 'false' : 'true'}
      >
        <span className="timeline-context-menu-row-content" data-menu-measure="main">
          <i className="bi bi-pencil timeline-context-menu-item-icon" />
          {labels.edit}
        </span>
      </button>

      <button
        type="button"
        className={`dropdown-item timeline-context-menu-measure-button text-danger${canDeleteWorkflow ? '' : ' disabled'}`}
        onClick={() => handleAction(onDeleteWorkflow, canDeleteWorkflow)}
        disabled={!canDeleteWorkflow}
        aria-disabled={canDeleteWorkflow ? 'false' : 'true'}
      >
        <span className="timeline-context-menu-row-content" data-menu-measure="main">
          <i className="bi bi-trash timeline-context-menu-item-icon" />
          {labels.delete}
        </span>
      </button>
    </div>
  );
}
